import { useEffect } from 'react'
import { Link, Navigate, Route, Routes } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { PartnerLayout } from '@/components/layout/PartnerLayout'
import { RequirePartner } from '@/components/layout/RouteGuards'
import { Toaster } from '@/components/ui/Feedback'
import { AccountPage } from '@/features/account/AccountPage'
import { LoginPage } from '@/features/auth/LoginPage'
import { CouponsPage } from '@/features/coupons/CouponsPage'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { EarningsPage } from '@/features/earnings/EarningsPage'
import { SettlementsPage } from '@/features/settlements/SettlementsPage'
import { UsersPage } from '@/features/users/UsersPage'
import { useAuthStore } from '@/store/auth'

export default function App() {
  // Signing out drops every cached figure, so the next partner on this browser never sees them.
  const status = useAuthStore((state) => state.status)
  const queryClient = useQueryClient()
  useEffect(() => {
    if (status === 'anonymous') queryClient.clear()
  }, [status, queryClient])

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<RequirePartner />}>
          <Route element={<PartnerLayout />}>
            <Route index element={<DashboardPage />} />
            <Route path="coupons" element={<CouponsPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="earnings" element={<EarningsPage />} />
            <Route path="settlements" element={<SettlementsPage />} />
            <Route path="account" element={<AccountPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster />
    </>
  )
}

function NotFound() {
  return (
    <div className="flex flex-col items-center px-4 py-20 text-center">
      <p className="num rounded-pill bg-accent-soft px-3 py-1 text-[12px] font-medium text-ink-soft">404</p>
      <h1 className="mt-4 text-[22px] font-semibold tracking-[-0.02em] text-ink">Page not found</h1>
      <p className="mt-1 text-[14px] text-ink-muted">That link does not go anywhere.</p>
      <Link to="/" className="mt-6 inline-flex h-11 items-center rounded-field bg-ink px-5 text-[15px] font-medium text-oncolor hover:bg-ink/90">
        Back to the dashboard
      </Link>
    </div>
  )
}
